/** English UI catalog. Runtime English emitters must match these values exactly
 *  when they flow through {@link localizeRuntimeMessage}. */
export const en = {
  "app.title": "Rescript — edit videos like you edit text",
  "common.cancel": "Cancel",
  "common.close": "Close",
  "common.delete": "Delete",
  "common.download": "Download",
  "common.import": "Import",
  "common.loading": "Loading",
  "common.remove": "Remove",
  "common.restore": "Restore",
  "common.retry": "Try again",
  "common.searchOrCreate": "Search or create…",
  "common.settings": "Settings",
  "common.system": "System",
  "common.tools": "Tools",
  "settings.appearance": "Appearance",
  "settings.light": "Light",
  "settings.dark": "Dark",
  "settings.interfaceLanguage": "Interface language",
  "settings.privacy": "Privacy",
  "settings.helpImprove": "Help improve the app",
  "settings.telemetryHelp": "Send anonymous feature usage statistics and crash reports.",
  "settings.support": "Support / feedback",
  "settings.reportIssue": "Report an issue",
  "settings.homepage": "Homepage",
  "settings.github": "GitHub",
  "settings.followX": "Follow on X",
  "model.transcriptSource": "Transcript source",
  "model.language": "Language",
  "model.transcriptLanguage": "Transcript language",
  "model.importTranscript": "Import transcript",
  "upload.recentProjects": "Recent projects",
  "upload.removeRecent": "Remove from recent",
  "upload.dropPrefix": "Drop a video or audio file here, or",
  "upload.browse": "browse",
  "upload.chooseTranscriptFirst": "Pick a transcript in the menu above, then drop your media",
  "upload.willUseTranscript": "Will use {name} · MP4, WebM, MOV, MP3, WAV, …",
  "upload.mediaFormats": "MP4, WebM, MOV, MP3, WAV, M4A, …",
  "upload.gettingReady": "Getting things ready",
  "upload.gettingReadyHelp": "Setting up the media engine, this only happens once.",
  "upload.unsupported": "This browser can't run the editor",
  "upload.unsupportedHelp":
    "Editing needs SharedArrayBuffer, which requires a cross-origin-isolated page. Try a recent Chrome, Edge, Safari or Firefox over HTTPS.",
  "upload.unsupportedSimd":
    "This device can't run the editor",
  "upload.unsupportedSimdHelp":
    "Editing needs WebAssembly SIMD, which this processor doesn't report support for. On a Windows on Arm PC, update to the latest Rescript — current installers include a native Arm64 build that doesn't run under emulation.",
  "upload.transcribeTitle": "Transcribe",
  "upload.transcribeText": "Whisper locally, or import SRT / VTT.",
  "upload.editTitle": "Edit",
  "upload.editText": "Select words and hit delete to edit.",
  "upload.exportTitle": "Export",
  "upload.exportText": "Render the final cut to MP4 or M4A.",
  "editor.chooseTranscript": "Choose a transcript file from the source menu first.",
  "editor.chooseMedia": "Please choose a video or audio file.",
  "editor.undo": "Undo (⌘Z)",
  "editor.redo": "Redo (⇧⌘Z)",
  "editor.export": "Export",
  "topbar.startOver": "Start over",
  "transcript.replace": "Replace transcript from SRT, VTT, or JSON",
  "transcript.header": "Transcript",
  "transcript.wordDeleted": "{count} word",
  "transcript.wordsDeleted": "{count} words",
  "transcript.replaceConfirm": "Replace the current transcript with this file?",
  "transcript.invalidFile": "Choose an SRT, VTT, or JSON transcript file.",
  "transcript.noSpeech": "No speech detected. Make sure this file has audio, or import a transcript.",
  "transcript.cut": "Cut",
  "transcript.follow": "Follow playhead",
  "transcript.hideDeleted": "Hide deleted words",
  "transcript.showDeleted": "Show deleted words",
  "transcript.correct": "Correct",
  "transcript.scrollWithPlayhead": "Scroll with the playhead",
  "transcript.joinSplit": "Clip split — click to join these clips",
  "transcript.joinClips": "Join clips",
  "transcript.hesitation": "Detected hesitation (not transcribed) — cut with Remove filler words",
  "transcript.textLabel": "Transcript text",
  "transcript.textHint": "Arrow keys move through the words and seek the playhead. Shift with arrows selects. Backspace or Delete cuts words, Enter corrects them, Space plays or pauses, and Undo brings cuts back.",
  "transcript.textHesitation": "[hesitation]",
  "transcript.textCut": "Cut: {text}",
  "transcript.textNothingToCut": "Nothing to cut",
  "transcript.textUndo": "Undo",
  "transcript.textRedo": "Redo",
  "tools.bulk": "Bulk transcript cleanups",
  "tools.removeFillers": "Remove filler words",
  "tools.removeFillersTitle": 'Cut filler words ("um", "uh", "...", …) from the video',
  "tools.restoreFillers": "Restore filler words",
  "tools.restoreFillersTitle": "Bring every cut filler word back",
  "tools.removeSilences": "Remove silences",
  "tools.removeSilencesTitle": "Cut pauses and silences (≥{seconds}s) from the video",
  "tools.restoreSilences": "Restore silences",
  "tools.restoreSilencesTitle": "Bring every cut pause back",
  "timeline.removed": "{trimmed} removed — original length {duration}",
  "timeline.back5": "Back 5 s",
  "timeline.playPause": "Play / pause (space)",
  "timeline.forward5": "Forward 5 s",
  "timeline.split": "Split",
  "timeline.splitTitle": "Split clip at playhead (S)",
  "timeline.splitDisabled": "Move the playhead onto a kept region to split",
  "timeline.delete": "Delete",
  "timeline.deleteTitle": "Delete selected clip (Delete)",
  "timeline.deleteDisabled": "Select a clip on the timeline to delete",
  "timeline.restore": "Restore",
  "timeline.restoreTitle": "Restore selected cut (Delete)",
  "timeline.restoreDisabled": "Select a cut or silence section on the timeline to restore",
  "timeline.zoomOut": "Zoom out",
  "timeline.fit": "Fit to window",
  "timeline.zoomIn": "Zoom in — drag word edges to refine timing",
  "timeline.joinClips": "Join these clips (remove split)",
  "timeline.trimStart": "Trim clip start",
  "timeline.trimEnd": "Trim clip end",
  "timeline.hesitationAdjust": "… detected hesitation — drag edges to adjust, or Remove filler words to cut",
  "timeline.hesitationCut": "… detected hesitation — cut with Remove filler words",
  "timeline.dragTiming": "{word} — drag edges to adjust timing",
  "timeline.scrollZoom": "Scroll to zoom in/out",
  "speaker.moveStart": "Drag to move where this speaker starts",
  "speaker.moveLabel": "Move speaker label",
  "speaker.options": "Speaker options",
  "speaker.change": "Change speaker",
  "speaker.rename": "Rename speaker",
  "speaker.renameAction": "Rename",
  "speaker.replace": "Replace in project with…",
  "speaker.removeProject": "Remove from project",
  "speaker.newName": "New name",
  "speaker.find": "Find a speaker…",
  "speaker.noMatches": "No matching speakers",
  "speaker.renameTo": "Rename {current} to {next}",
  "speaker.button": "Speaker",
  "speaker.search": "Search or create…",
  "speaker.defaultName": "Speaker {number}",
  "speaker.create": 'Create "{name}"',
  "export.title": "Export",
  "export.type": "Export type",
  "export.video": "Video",
  "export.audio": "Audio",
  "export.transcript": "Transcript",
  "export.subtitles": "Subtitles",
  "export.videoUnavailable": "Video export isn’t available for audio-only projects",
  "export.noAudio": "This file has no audio track",
  "export.noWordsFirst": "Transcribe or import a transcript first",
  "export.format": "Format",
  "export.resolution": "Resolution",
  "export.original": "Original",
  "export.plainText": "TXT",
  "export.markdown": "Markdown",
  "export.statOriginal": "Original",
  "export.statCuts": "Cuts",
  "export.statEdited": "Edited",
  "export.includeTimestamps": "Include timestamps",
  "export.speakerLabels": "Include speaker names",
  "export.shortCues": "Short cues (≤5s)",
  "export.transcriptHelp": "Speaker-labeled text with cuts removed.",
  "export.transcriptHelpTimestamps":
    "Speaker-labeled text with cuts removed. Each turn starts with its edited-timeline time.",
  "export.transcriptHelpPdf":
    "PDF uses a standard Latin font — prefer DOCX for full Unicode (CJK, etc.).",
  "export.subtitlesHelp":
    "SRT and VTT use the edited timeline (cuts applied). JSON keeps the full word list for re-import.",
  "export.subtitlesHelpShortCues":
    "SRT and VTT use the edited timeline (cuts applied), split into short sentence-sized cues (≤5s). JSON keeps the full word list for re-import.",
  "export.encodingHelp": "Re-encoding with ffmpeg.wasm — longer files take a while.",
  "export.reexport": "Re-export with latest edits",
  "export.rendering": "Rendering in your browser…",
  "export.downloadFile": "Download {name}",
  "export.saveFile": "Save {name}…",
  "export.saving": "Saving…",
  "export.saveDialogTitle": "Save export",
  "export.savedAs": "Saved as {name}",
  "export.showInFinder": "Show in Finder",
  "export.showInExplorer": "Show in Explorer",
  "export.showInFolder": "Show in folder",
  "export.renderingNative": "Rendering…",
  "export.encodingHelpNative": "Re-encoding with ffmpeg — longer files take a while.",
  "export.downloadFormat": "Download .{format}",
  "export.exportFormat": "Export {format}",
  "export.timeline": "Timeline",
  "export.timelineTitle":
    "Export an NLE / DAW sequence (Resolve, Premiere, Final Cut, Pro Tools, Reaper, Blender)",
  "export.nle": "Format",
  "export.frameRate": "Frame rate",
  "export.timelineHelp":
    "Sequence references your original media by filename — relink in the NLE after import.",
  "export.timelineHelpAaf": "Pro Tools / Logic AAF (metadata-only).",
  "export.timelineHelpFcpx": "Final Cut Pro FCPXML.",
  "export.timelineHelpPremiere": "Adobe Premiere Pro XML (xmeml).",
  "export.timelineHelpResolve": "DaVinci Resolve XML (xmeml).",
  "export.timelineHelpReaper": "Reaper project (.rpp).",
  "export.timelineHelpSamplitude":
    "Samplitude EDL (.edl) — also opens directly in Reaper.",
  "export.timelineHelpBlender":
    "Blender video editing project (.blend, Blender 5.2+) — save it in the same folder as the original media.",
  "export.aafOverCap":
    "This edit has {count} clips; AAF supports up to {max}. Use Resolve, Premiere, Final Cut, Reaper, or Samplitude EDL instead.",
  "export.preparing": "Preparing…",
  "import.reading": "Reading transcript…",
  "import.importing": "Import…",
  "import.failed": "Import failed",
  "import.readingFile": "Reading file…",
  "import.chooseFileShort": "Choose a file…",
  "import.chooseFile": "Choose an SRT, VTT, or JSON transcript",
  "import.selected": "{name} selected",
  "banner.faster": "Want faster transcription and exports?",
  "banner.getDesktop": "Get the Rescript desktop app",
  "banner.downloadFor": "Download for {platform}",
  "banner.dismiss": "Dismiss",
  "social.githubRepo": "GitHub repository",
  "social.discordServer": "Discord server",
  "social.xProfile": "X profile",
  "globalError.title": "Rescript — something went wrong",
  "globalError.heading": "Something went wrong",
  "globalError.body":
    "The editor hit an unexpected error. Your saved projects are still on this device — reloading should bring them back.",
  "progress.loadingMedia": "Loading media…",
  "progress.loadingMediaEngine": "Loading media engine…",
  "progress.extractingAudio": "Extracting audio…",
  "progress.preparingMedia": "Preparing media…",
  "progress.loadingSpeechModel": "Loading speech model…",
  "progress.loadingSpeechCache": "Loading speech model from cache…",
  "progress.downloadingSpeech": "Downloading speech model…",
  "progress.gpuFallback": "GPU interrupted — continuing on CPU…",
  "progress.detectingSpeech": "Detecting speech…",
  "progress.transcribing": "Transcribing…",
  "progress.loadingAlignCache": "Loading alignment model from cache…",
  "progress.downloadingAlign": "Downloading alignment model…",
  "progress.aligning": "Aligning words…",
  "progress.speakers": "Identifying speakers…",
  "error.selectModel": "Select a speech model to transcribe.",
  "error.workerCrashed": "Transcription worker crashed.",
  "error.mediaEngineNetwork":
    "Couldn't load the media engine — the connection dropped. Check your internet and try again.",
  "error.mediaEngineStalled":
    "The media engine stopped responding and was restarted. Close other apps and tabs to free up memory, then try again.",
  "error.mediaEngineNotReady":
    "The media engine isn't ready yet — reload the page and try again.",
  "error.simdUnsupported":
    "This processor doesn't support WebAssembly SIMD, which the media engine needs. On a Windows on Arm PC, update to the latest Rescript — current installers include a native Arm64 build that doesn't run under emulation.",
  "error.processFile": "Failed to process this file.",
  "error.extractAudio": "Could not extract audio from this file.",
  "error.nothingToExport": "Everything has been deleted — nothing to export.",
  "error.videoExport": "Export failed while rendering the video.",
  "error.audioExport": "Export failed while rendering the audio.",
  "error.exportSave": "Couldn't save the exported file.",
  "error.diskFull": "There isn't enough free disk space to finish. Free up some space and try again.",
  "error.export": "Export failed.",
  "error.timelineExport": "Timeline export failed.",
  "error.timelineEmpty": "Nothing to put on the timeline.",
  "error.aafTemplate": "Could not load the AAF export template.",
  "error.emptyTranscript": "That transcript file is empty.",
  "error.noTimedWords": "No timed words found in that transcript.",
  "error.parseJson": "Could not parse that JSON transcript.",
  "error.jsonShape": 'JSON must be a word array or { "words": [...] }.',
  "error.noWords": "No words to export.",
  "error.projectMissing": "That project is no longer saved.",
  "error.openProject": "Could not open that project.",
  "error.removeProject": "Could not remove that project.",
  "error.readTranscript": "Could not read that transcript.",
  "error.clearRecent": "Could not clear recent projects.",
  "error.modelDownload":
    "Couldn't finish downloading the speech model — the connection dropped. Check your internet and try again; the parts that finished downloading are kept.",
  "error.gpuReset":
    "Transcription was interrupted when the GPU reset (often after locking the screen). Please try again.",
  "confirm.clearRecent": "Remove all recent projects? Their saved edits are deleted.",
} as const;

export type MessageKey = keyof typeof en;
